import { setRegisteredData } from "./registered-data";
import {
    startDrop,
    teardownDrop,
    pauseDrop,
    resumeDrop,
    preloadDropAssets,
} from "../../../games/drop/drop";

/**
 * In-modal navigation for the multi-step application flow (Drop).
 *
 * Screens live in #modal-form-application as .modal-screen[data-screen="..."] and are
 * shown one at a time. Navigation is driven by data attributes on buttons:
 *   - data-modal-next="<screen>"   → simply show that screen
 *   - data-modal-action="<action>" → run a behaviour (currently only start-game)
 *
 * Flow: loading → instr-1 … instr-7 → game → result | result-bad → thanks, plus "expired".
 * instr-3..instr-6 have no button: they carry data-modal-next on the screen element itself, so a
 * tap anywhere advances. instr-7 has the button that starts the real game.
 * The result step branches on RESULT_SCORE_THRESHOLD: "result" at or above it, "result-bad" below.
 */

const MODAL_SELECTOR = '#modal-form-application';
const MIN_LOADING_MS = 1500; // keep the loading screen up at least this long, even if assets cache instantly
const LOADING_AUTO_PROCEED = true; // TEMP: set true to re-enable auto-advance to confirmation (disabled for styling)
// The game must be started within 2 h of submitting the entry (enforced server-side in
// DropController::start). Mirror that window client-side: auto-hide the stale modal after 2 h,
// unless the real game has already begun (the timer is cleared on game start).
const MODAL_AUTO_HIDE_MS = 2 * 60 * 60 * 1000;
// Screens that hide the shared lives/score HUD: the loading screen, the game (the Drop engine
// draws its own HUD there) and the two end screens, where the run is over.
const HUD_HIDDEN_SCREENS = ['loading', 'game', 'expired', 'thanks'];
// Shown in the HUD until the real game produces a score (_score stays null before then), so the
// pre-game screens read "Punkty: 0".
const HUD_PRE_GAME_SCORE = 0;
// The result flow branches on the final score: at or above this it is a win ("result"), below it
// the consolation screen ("result-bad").
const RESULT_SCORE_THRESHOLD = 200;
// Terminal screens: their "Zakończ" link is meant to navigate away, so the unload guard below
// stays quiet on them. Every other screen means an entry in progress.
const UNLOAD_ALLOWED_SCREENS = ['thanks', 'expired'];

let _data = null;
let _uuid = null;
let _score = null;
let _autoHideTimer = null;
let _currentScreen = null;

const $modal = () => $(MODAL_SELECTOR);
const csrf = () => $modal().data('game-csrf');

function show(screen) {
    const $m = $modal();
    _currentScreen = screen;
    $m.find('.modal-screen').addClass('d-none');
    $m.find(`.modal-screen[data-screen="${screen}"]`).removeClass('d-none');
    $m.find('.modal-dev-screen').val(screen);

    // The HUD lives outside the screens (one node, not one per screen), so it is toggled here.
    $m.find('.modal-hud')
        .toggleClass('d-none', HUD_HIDDEN_SCREENS.includes(screen))
        .find('.modal-hud__score-value').text(_score ?? HUD_PRE_GAME_SCORE);

    if (screen === 'thanks') {
        renderThanksScore();
    }
}

/** Entry point: called from the form submit-success handler. */
export function startApplicationModal(data) {
    _data = data;
    _uuid = data.uuid;
    _score = null;

    // The submit handler suppresses ajax-form's post-success reset (see application.js), so do
    // it here: the native reset clears the fields AND fires the 'reset' event that mints a fresh
    // idempotency key (see form/idempotency.js), so a follow-up entry isn't rejected as a dupe.
    resetApplicationForm();

    $modal().modal('show');
    armAutoHide();
    runLoading();
}

/** Arm the 2 h auto-hide: once the start window passes, show the "start expired" screen on the
 *  (unstarted) modal — matching what the server would return if the user tried to start now. */
function armAutoHide() {
    clearAutoHide();
    _autoHideTimer = setTimeout(() => {
        _autoHideTimer = null;
        show('expired');
    }, MODAL_AUTO_HIDE_MS);
}

/** Cancel the auto-hide timer (e.g. once the real game has started, or the modal closed). */
function clearAutoHide() {
    if (_autoHideTimer !== null) {
        clearTimeout(_autoHideTimer);
        _autoHideTimer = null;
    }
}

/** Reset the underlying page form; its native 'reset' event regenerates the idempotency key. */
function resetApplicationForm() {
    $('.application-form').each(function () {
        this.reset();
    });
}

/**
 * Show the loading screen and fill the 8-note progress bar in step with the actual asset
 * preload (background, catcher, sprites, HUD icons). Each note represents 12.5 % of the
 * assets; once everything is cached, reveal the instr-1 screen.
 */
function runLoading() {
    show('loading');

    const startedAt = performance.now();
    const $bar = $modal().find('.loading-notes');
    const $notes = $bar.find('.loading-note').removeClass('is-full');
    const total = $notes.length || 1;
    $bar.attr('aria-valuenow', 0);

    // Scatter the notes over the staff height. --note-y is a signed fraction of the free
    // vertical space (see .loading-note in s-game-modal.scss), re-rolled on every run so the
    // bar never reads as a fixed pattern.
    $notes.each(function () {
        this.style.setProperty('--note-y', (Math.random() * 2 - 1).toFixed(3));
    });

    const paint = (progress) => {
        const filled = Math.round(progress * total);
        $notes.each(function (i) {
            $(this).toggleClass('is-full', i < filled);
        });
        $bar.attr('aria-valuenow', filled);
    };

    // Kick off the asset preload, but drive the bar by elapsed time so it fills smoothly
    // and linearly over MIN_LOADING_MS rather than snapping to full on a cache hit.
    let preloadDone = false;
    preloadDropAssets().then(() => { preloadDone = true; });

    const tick = () => {
        const timeFrac = Math.min(1, (performance.now() - startedAt) / MIN_LOADING_MS);
        // Advance linearly with time; if assets aren't ready yet, hold just short of full.
        paint(preloadDone ? timeFrac : Math.min(timeFrac, 0.9));

        if (timeFrac >= 1 && preloadDone) {
            paint(1);
            if (!LOADING_AUTO_PROCEED) {
                return; // TEMP: stay on the loading screen for styling
            }
            if (_data) {
                setRegisteredData(_data, MODAL_SELECTOR);
            }
            show('instr-1');
            return;
        }
        requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
}

function gameContainer() {
    return $modal().find('#game-canvas-container').get(0);
}

/** Real game: the score is registered server-side by /drop/result; here we reveal the result. */
function startGame() {
    // The game is starting → the 2 h start window no longer applies; stop the auto-hide timer
    // so the modal can't vanish mid-game.
    clearAutoHide();
    show('game');
    startDrop(gameContainer(), {
        demo: false,
        uuid: _uuid,
        // Fires once the player dismisses the engine's own "koniec gry" modal, so this screen
        // never overlaps it.
        onDone: function (score) {
            _score = score;
            renderResult();
            show(resultScreen());
        },
        onError: function (error) {
            // /start can reject with a code we map to a dedicated screen; anything else falls
            // back to a neutral score-0 result.
            const code = error && error.data && error.data.error;
            if (code === 'start_window_expired') {
                return show('expired');
            }
            _score = 0;
            renderResult();
            show(resultScreen());
        },
    });
}

/**
 * Append the final score to the registered-data list on the thanks screen. It cannot come from
 * setRegisteredData(): that runs off the submit payload during loading, long before the game has
 * produced a score. Written on every show so a re-entry cannot leave a stale value behind.
 */
function renderThanksScore() {
    const $list = $modal().find('.modal-screen--thanks .registered-data-list');
    if (!$list.length) {
        return;
    }

    let $item = $list.find('.registered-data-score');
    if (!$item.length) {
        $item = $('<li class="registered-data-score"><strong>Wynik</strong>: <span></span></li>')
            .appendTo($list);
    }
    $item.find('span').text(_score ?? HUD_PRE_GAME_SCORE);
}

/** Which of the two result screens the final score lands on. */
function resultScreen() {
    return (_score ?? 0) < RESULT_SCORE_THRESHOLD ? 'result-bad' : 'result';
}

function renderResult() {
    // Both result screens carry .result-score, so this fills whichever one is about to show.
    $modal().find('.result-score').text(_score ?? 0);
}

// Stop the game and cancel the auto-hide timer whenever the modal is closed.
$(document).on('hidden.bs.modal', MODAL_SELECTOR, function () {
    clearAutoHide();
    teardownDrop();
    _currentScreen = null;
});

// Leaving the page mid-entry loses the run, so confirm first. The browser supplies its own
// wording — the returnValue is only the signal that a prompt is wanted, not the text shown.
// Skipped on the terminal screens, whose own link is supposed to navigate away.
window.addEventListener('beforeunload', function (event) {
    const $m = $modal();
    // Off in dev, where it would prompt on every reload while working on the modal.
    if ($m.is('[data-dev]')) {
        return;
    }
    if (!$m.hasClass('show') || UNLOAD_ALLOWED_SCREENS.includes(_currentScreen)) {
        return;
    }
    event.preventDefault();
    event.returnValue = '';
});

// Orientation guard: on short landscape (mobile) screens the modal shows a "rotate to
// portrait" cover (CSS-only, see s-game-modal.scss — must match this media query). While
// that cover is up over a running game, pause the engine and resume on rotate back.
const ROTATE_MQ = window.matchMedia('(orientation: landscape) and (max-height: 400px)');

function handleRotateGuard(event) {
    if (!$modal().hasClass('show')) {
        return;
    }
    if (event.matches) {
        pauseDrop();
    } else {
        resumeDrop();
    }
}

// WYŁĄCZONE razem z zaślepką „obróć telefon" (patrz .modal-rotate-cover w s-game-modal.scss).
// Bez widocznej zaślepki pauza w landscape zatrzymywałaby grę bez żadnego komunikatu,
// więc listener też jest odcięty. Żeby przywrócić: odkomentuj oba miejsca.
// if (ROTATE_MQ.addEventListener) {
//     ROTATE_MQ.addEventListener('change', handleRotateGuard);
// } else if (ROTATE_MQ.addListener) {
//     ROTATE_MQ.addListener(handleRotateGuard); // Safari < 14
// }

// DEV ONLY: jump to the chosen screen (re-runs loading / renders the result score).
$(document).on('change', `${MODAL_SELECTOR} .modal-dev-screen`, function () {
    const screen = $(this).val();
    teardownDrop();
    if (screen === 'loading') {
        return runLoading();
    }
    if (screen === 'result' || screen === 'result-bad') {
        renderResult();
    }
    show(screen);
});

// DEV ONLY: open the modal with a stub payload + hardcoded uuid, skipping the form submit.
$(document).on('click', '.application-modal-dev', function () {
    const uuid = $(this).data('dev-uuid');
    startApplicationModal({
        status: 'OK',
        uuid,
        // Mirrors what /api/application returns, so the thanks screen's "Drukuj" button resolves
        // here too instead of staying on href="#".
        pdf: `/api/${uuid}.pdf`,
        firstname: 'Jan',
        lastname: 'Kowalski',
        email: 'jan.kowalski@example.com',
        phone: '500600700',
        billType: 'Paragon',
        billNr: '0001',
        billDate: '01.01.2026',
        billNip: null,
        billPos: '01',
        prize: null,
        formUrl: null,
    });
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-next]`, function () {
    show($(this).data('modal-next'));
});

$(document).on('click', `${MODAL_SELECTOR} .thanks-actions__wide`, () => {
    location.reload();
});

$(document).on('click', `${MODAL_SELECTOR} [data-modal-action]`, function () {
    switch ($(this).data('modal-action')) {
        case 'start-game':
            return startGame();
    }
});
